/**
 * Composant SiteSelector
 * Sélection des sites WordPress avec options avancées
 */
import React from 'react';
import { Globe, Check, Settings2, Trash2, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';

export const SiteSelector = ({
  sites,
  selectedSites,
  orderedSites,
  siteTitles,
  sitePrompts,
  siteRewritePrompts,
  showAdvanced,
  titlesRequired = false,
  onToggleSite,
  onUpdateSiteTitle,
  onUpdateSitePrompt,
  onUpdateSiteRewritePrompt,
  onToggleAdvanced,
  onMoveSiteUp,
  onMoveSiteDown,
}) => {
  const getFaviconUrl = (siteUrl) => {
    try {
      const url = new URL(siteUrl);
      return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
    } catch {
      return null;
    }
  };

  return (
    <>
      {/* Sélection des sites */}
      <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
        <CardHeader>
          <CardTitle className="text-lg font-mono uppercase text-cyan-400 flex items-center">
            <Globe className="w-5 h-5 mr-2" />
            SÉLECTION_SITES
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sites.length === 0 ? (
            <p className="text-slate-400 text-center py-4">Aucun site configuré.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sites.map((site) => {
                const faviconUrl = getFaviconUrl(site.url);
                const isSelected = selectedSites.includes(site.id);
                const hasDefault = !!(site.default_prompt && site.default_prompt.trim());
                const hasOverride = !!(sitePrompts[site.id] && sitePrompts[site.id].trim());
                const hasRewrite = !!(siteRewritePrompts[site.id] && siteRewritePrompts[site.id].trim());
                const isAdvancedOpen = !!showAdvanced[site.id];
                
                return (
                  <div key={site.id} className="space-y-2">
                    {/* Site card */}
                    <div
                      onClick={() => onToggleSite(site.id)}
                      className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-cyan-500/10 border-cyan-500/50'
                          : 'bg-slate-900/30 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="mr-2 border-cyan-500 data-[state=checked]:bg-cyan-500"
                      />
                      <div className="w-8 h-8 rounded bg-slate-900 border border-slate-700 flex items-center justify-center overflow-hidden mr-2">
                        <img src={faviconUrl} alt="" className="w-6 h-6 object-contain" onError={(e) => e.target.style.display = 'none'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-cyan-400 font-semibold text-sm truncate">{site.name}</p>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-cyan-400 shrink-0" />}
                    </div>
                    
                    {/* Options du site sélectionné */}
                    {isSelected && (
                      <SiteOptions
                        site={site}
                        siteTitles={siteTitles}
                        sitePrompts={sitePrompts}
                        siteRewritePrompts={siteRewritePrompts}
                        showAdvanced={showAdvanced}
                        hasDefault={hasDefault}
                        hasOverride={hasOverride}
                        hasRewrite={hasRewrite}
                        isAdvancedOpen={isAdvancedOpen}
                        titlesRequired={titlesRequired}
                        onUpdateSiteTitle={onUpdateSiteTitle}
                        onUpdateSitePrompt={onUpdateSitePrompt}
                        onUpdateSiteRewritePrompt={onUpdateSiteRewritePrompt}
                        onToggleAdvanced={onToggleAdvanced}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ordre de génération */}
      {orderedSites.length > 1 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-purple-400 font-mono uppercase tracking-wider flex items-center gap-2">
              <GripVertical className="w-4 h-4" />
              Ordre de génération
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {orderedSites.map((site, index) => (
                <div key={site.id} className="flex items-center gap-2 p-2 bg-slate-800/50 rounded border border-slate-700">
                  <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-purple-400 bg-purple-500/20 rounded">
                    {index + 1}
                  </span>
                  <span className="flex-1 text-sm text-slate-300">{site.name}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveSiteUp(index); }}
                      disabled={index === 0}
                      className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowUp className="w-4 h-4 text-slate-400" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveSiteDown(index); }}
                      disabled={index === orderedSites.length - 1}
                      className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowDown className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// Sous-composant pour les options du site
const SiteOptions = ({
  site,
  siteTitles,
  sitePrompts,
  siteRewritePrompts,
  hasDefault,
  hasOverride,
  hasRewrite,
  isAdvancedOpen,
  titlesRequired = false,
  onUpdateSiteTitle,
  onUpdateSitePrompt,
  onUpdateSiteRewritePrompt,
  onToggleAdvanced,
}) => {
  const titleValue = siteTitles[site.id] || '';
  const titleMissing = titlesRequired && !titleValue.trim();
  
  return (
    <div className="pl-2 mt-2 space-y-2">
      {/* Titre pour ce site */}
      <div>
        {titlesRequired && (
          <label className="text-[10px] text-slate-500 uppercase font-mono mb-1 block flex items-center gap-1">
            Titre <span className="text-red-400">*</span>
          </label>
        )}
        <Textarea
          value={titleValue}
          onChange={(e) => {
            onUpdateSiteTitle(site.id, e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          placeholder={titlesRequired ? "Titre obligatoire pour ce site..." : "Titre pour ce site (optionnel)..."}
          className={`bg-slate-950/50 border-slate-700 text-slate-200 placeholder:text-slate-600 text-sm min-h-[36px] resize-none overflow-hidden ${titleMissing ? 'border-red-500/50' : ''}`}
          style={{ height: 'auto' }}
          data-testid={`user-title-input-${site.id}`}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          rows={1}
        />
      </div>
      {titleValue.length > 60 && (
        <div className="flex items-center gap-1 text-amber-400 text-xs">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>{titleValue.length} car. — SEO à optimiser</span>
        </div>
      )}

      {/* Badges statut */}
      <div className="flex items-center gap-1 flex-wrap pt-1">
        {hasDefault && (
          <span className="text-[10px] bg-green-500/15 text-green-400 px-1.5 py-0.5 rounded font-mono">Défaut</span>
        )}
        {hasRewrite && (
          <span className="text-[10px] bg-cyan-500/15 text-cyan-400 px-1.5 py-0.5 rounded font-mono">Détails</span>
        )}
        {hasOverride && (
          <span className="text-[10px] bg-red-500/15 text-red-400 px-1.5 py-0.5 rounded font-mono">Override</span>
        )}
      </div>

      {/* Détails de rédaction (rewrite prompt) */}
      <Textarea
        value={siteRewritePrompts[site.id] || ''}
        onChange={(e) => {
          onUpdateSiteRewritePrompt(site.id, e.target.value);
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
        placeholder="Précisions sur le ton, l'angle, le style souhaité pour cet article..."
        className="bg-slate-950/50 border-cyan-800/30 text-slate-100 placeholder:text-slate-600 text-sm min-h-[44px] resize-none overflow-hidden focus:border-cyan-600/50"
        style={{ height: 'auto' }}
        data-testid={`site-rewrite-prompt-${site.id}`}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => {
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
        rows={2}
      />

      {/* Bouton réglages avancés */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleAdvanced(site.id); }}
        className={`text-[10px] flex items-center gap-1 transition-colors ${
          isAdvancedOpen || hasOverride ? 'text-red-400' : 'text-red-500/40 hover:text-red-400/70'
        }`}
        data-testid={`advanced-toggle-${site.id}`}
      >
        <Settings2 className="w-3 h-3" />
        {isAdvancedOpen ? 'Masquer les réglages avancés' : 'Réglages avancés'}
      </button>

      {/* Section avancée (override du prompt par défaut) */}
      {isAdvancedOpen && (
        <div className="p-2.5 rounded border border-red-500/20 bg-red-950/10" onClick={(e) => e.stopPropagation()}>
          <p className="text-[10px] text-red-400/70 mb-1.5 uppercase tracking-wider font-semibold">Remplacer le prompt par défaut</p>
          {hasDefault && (
            <div className="mb-2 p-2 bg-slate-800/50 rounded text-xs text-slate-500 font-mono whitespace-pre-wrap max-h-16 overflow-y-auto">
              {site.default_prompt}
            </div>
          )}
          <Textarea
            value={sitePrompts[site.id] || ''}
            onChange={(e) => {
              onUpdateSitePrompt(site.id, e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            placeholder={hasDefault ? "Laisser vide pour le défaut..." : "Instructions spécifiques..."}
            className="bg-slate-950/50 border-red-800/30 text-slate-100 placeholder:text-slate-600 font-mono text-xs min-h-[40px] resize-none overflow-hidden"
            style={{ height: 'auto' }}
            data-testid={`site-prompt-${site.id}`}
            onFocus={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            rows={2}
          />
          {hasOverride && (
            <button
              onClick={() => onUpdateSitePrompt(site.id, '')}
              className="text-[10px] text-slate-500 hover:text-red-400 mt-1 flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Restaurer le défaut
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SiteSelector;
